import { CSSObject } from '@mui/material';

import {
  Icons,
  colorOrToken,
  mergeSx,
  StatusChip,
  StatusChipProps,
  tokens,
} from '@cast/design-system';
import { WebhookStatus, SlackStatus } from '@cast/types';

const colors: Record<string, CSSObject> = {
  // Webhook statuses
  [WebhookStatus.CONNECTED]: {
    color: colorOrToken('green.600', tokens.theme.fg.status.onSuccessSubtle),
    backgroundColor: colorOrToken(
      'green.50',
      tokens.theme.bg.status.successSubtle
    ),
  },
  [WebhookStatus.FAILED]: {
    color: colorOrToken('red.500', tokens.theme.fg.status.onErrorSubtle),
    backgroundColor: colorOrToken('red.50', tokens.theme.bg.status.errorSubtle),
  },
  // Slack statuses
  [SlackStatus.OK]: {
    color: colorOrToken('green.600', tokens.theme.fg.status.onSuccessSubtle),
    backgroundColor: colorOrToken(
      'green.50',
      tokens.theme.bg.status.successSubtle
    ),
  },
  [SlackStatus.DEGRADED]: {
    color: colorOrToken('yellow.500', tokens.theme.fg.status.onWarningSubtle),
    backgroundColor: colorOrToken(
      'yellow.50',
      tokens.theme.bg.status.warningSubtle
    ),
  },
  [SlackStatus.NOT_OK]: {
    color: colorOrToken('red.500', tokens.theme.fg.status.onErrorSubtle),
    backgroundColor: colorOrToken('red.50', tokens.theme.bg.status.errorSubtle),
  },
  [SlackStatus.UNKNOWN]: {
    color: colorOrToken('grey.500', tokens.theme.fg.status.onNeutralSubtle),
    backgroundColor: colorOrToken(
      'grey.50',
      tokens.theme.bg.status.neutralSubtle
    ),
  },
};

type Props = StatusChipProps & {
  status?: string;
  noText?: boolean;
};

const renderText = (status: Props['status']) => {
  switch (status) {
    // Webhook statuses
    case WebhookStatus.CONNECTED: {
      return <span className="StatusChip-text">Can connect</span>;
    }
    case WebhookStatus.FAILED: {
      return <span className="StatusChip-text">Cannot connect</span>;
    }
    // Slack statuses
    case SlackStatus.OK: {
      return <span className="StatusChip-text">Can connect</span>;
    }
    case SlackStatus.DEGRADED: {
      return <span className="StatusChip-text">Degraded delivery</span>;
    }
    case SlackStatus.NOT_OK: {
      return <span className="StatusChip-text">Cannot connect</span>;
    }
    case SlackStatus.UNKNOWN: {
      return <span className="StatusChip-text">Unknown</span>;
    }
    case 'UNKNOWN':
      return null;
  }
  return undefined;
};

const renderIcon = (status: Props['status']) => {
  switch (status) {
    // Webhook statuses
    case WebhookStatus.CONNECTED: {
      return <Icons.CheckCircle />;
    }
    case WebhookStatus.FAILED: {
      return <Icons.XCircle />;
    }
    // Slack statuses
    case SlackStatus.OK: {
      return <Icons.CheckCircle />;
    }
    case SlackStatus.DEGRADED: {
      return <Icons.Warning />;
    }
    case SlackStatus.NOT_OK: {
      return <Icons.XCircle />;
    }
    case SlackStatus.UNKNOWN: {
      return <Icons.Question />;
    }
    case 'UNKNOWN':
      return null;
  }
  return undefined;
};

export const WebhookStatusChip = ({
  status = 'UNKNOWN',
  noText,
  sx,
  ...props
}: Props) => {
  return (
    <StatusChip
      iconChip={noText}
      size="md"
      sx={mergeSx(colors[status], sx, { bg: 'none' })}
      startIcon={!noText ? renderIcon(status) : undefined}
      {...props}
    >
      {!noText ? renderText(status) : renderIcon(status)}
    </StatusChip>
  );
};
